import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Menu,
  MenuItem as MenuItemComponent,
  Stack,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as ProjectIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ContactMail as ContactIcon,
  Receipt as BillingIcon,
  Message as MessageIcon,
  Call as CallIcon,
  Group as MeetingIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import { useLoading } from '../../contexts/LoadingContext';
import { useNotify } from '../ui/NotificationSystem';
import { clientService, Client, ClientStats, ClientProject } from '../../services/ClientService';

interface CommunicationLog {
  id: string;
  type: 'email' | 'call' | 'meeting' | 'note';
  subject: string;
  content: string;
  date: string;
  participants: string[];
  attachments?: string[];
  createdBy: string;
}

interface ClientContact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  isPrimary: boolean;
  department?: string;
}

interface BillingRecord {
  id: string;
  invoiceNumber: string;
  amount: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  issueDate: string;
  dueDate: string;
  paidDate?: string;
  description: string;
  projectId?: number;
}

const ClientManagement: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [clientDetailDialog, setClientDetailDialog] = useState(false);
  const [selectedClientForDetail, setSelectedClientForDetail] = useState<Client | null>(null);
  
  const { setLoading } = useLoading();
  const { success: notifySuccess, error: notifyError } = useNotify();

  useEffect(() => {
    loadClients();
    loadStats();
  }, []);

  // Reload clients when search or filter changes
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadClients();
    }, 300); // Debounce search to avoid too many API calls

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, filterStatus]);

  const loadClients = async () => {
    setLoading('clients', true);
    try {
      const response = await clientService.getClients({
        search: searchTerm,
        status: filterStatus,
        per_page: 50 // Load more clients for better UX
      });
      setClients(response.clients);
    } catch (error) {
      console.error('Failed to load clients:', error);
      notifyError('Failed to load clients');
    } finally {
      setLoading('clients', false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await clientService.getClientStats();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load client statistics:', error);
      notifyError('Failed to load client statistics');
    }
  };

  const handleCreateClient = () => {
    navigate('/dashboard/admin/clients/create');
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setOpenDialog(true);
    setAnchorEl(null);
  };

  const handleDeleteClient = async (client: Client) => {
    if (window.confirm(`Are you sure you want to delete client "${client.name}"?`)) {
      try {
        await clientService.deleteClient(client.id);
        setClients(prev => prev.filter(c => c.id !== client.id));
        notifySuccess('Client deleted successfully');
        loadStats(); // Refresh stats after deletion
      } catch (error) {
        console.error('Failed to delete client:', error);
        notifyError('Failed to delete client');
      }
    }
    setAnchorEl(null);
  };

  const handleChangeStatus = async (client: Client, newStatus: 'active' | 'inactive' | 'suspended') => {
    try {
      const updatedClient = await clientService.changeClientStatus(client.id, newStatus);
      setClients(prev => prev.map(c => 
        c.id === client.id ? updatedClient : c
      ));
      notifySuccess(`Client status changed to ${newStatus}`);
      loadStats(); // Refresh stats after status change
    } catch (error) {
      console.error('Failed to change client status:', error);
      notifyError('Failed to change client status');
    }
    setAnchorEl(null);
  };

  const handleSaveClient = async (clientData: Partial<Client>) => {
    setLoading('saveClient', true);
    try {
      if (editingClient) {
        // Update existing client
        const updatedClient = await clientService.updateClient(editingClient.id, clientData);
        setClients(prev => prev.map(c => 
          c.id === editingClient.id ? updatedClient : c
        ));
        notifySuccess('Client updated successfully');
      } else {
        // Create new client
        const newClient = await clientService.createClient(clientData as any);
        setClients(prev => [...prev, newClient]);
        notifySuccess('Client created successfully');
      }
      setOpenDialog(false);
      loadStats(); // Refresh stats after save
    } catch (error) {
      console.error('Failed to save client:', error);
      notifyError('Failed to save client');
    } finally {
      setLoading('saveClient', false);
    }
  };

  const handleViewClient = (client: Client) => {
    setSelectedClientForDetail(client);
    setClientDetailDialog(true);
  };

  const handleClientNameClick = (client: Client) => {
    handleViewClient(client);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircleIcon />;
      case 'inactive': return <ScheduleIcon />;
      case 'suspended': return <BlockIcon />;
      default: return <CheckCircleIcon />;
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (client.company && client.company.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, client: Client) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClient(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Client Management
      </Typography>

      {/* Client Statistics */}
      {stats && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
          gap: 3, 
          mb: 3 
        }}>
          <Box>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" color="primary">
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Clients
                    </Typography>
                  </Box>
                  <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" color="success.main">
                      {stats.active}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Clients
                    </Typography>
                  </Box>
                  <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" color="info.main">
                      ${(stats.totalRevenue / 1000).toFixed(0)}K
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Revenue
                    </Typography>
                  </Box>
                  <MoneyIcon color="info" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" color="warning.main">
                      ${(stats.averageProjectValue / 1000).toFixed(0)}K
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Project Value
                    </Typography>
                  </Box>
                  <ProjectIcon color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Actions and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flex: 1 }}>
              <TextField
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<ContactIcon />}
                onClick={() => navigate('/dashboard/admin/clients/portal')}
                sx={{ minWidth: 180 }}
              >
                Client Portal Access
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateClient}
                sx={{ minWidth: 150 }}
              >
                Add Client
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact Info</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Projects</TableCell>
                  <TableCell>Total Spent</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {client.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              cursor: 'pointer', 
                              color: 'primary.main',
                              '&:hover': { 
                                textDecoration: 'underline',
                                color: 'primary.dark'
                              }
                            }}
                            onClick={() => handleClientNameClick(client)}
                          >
                            {client.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {client.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{client.email}</Typography>
                        </Box>
                        {client.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2">{client.phone}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {client.company ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{client.company}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No company
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(client.status)}
                        label={client.status}
                        color={getStatusColor(client.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          <strong>{client.activeProjects}</strong> active
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {client.totalProjects} total
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        ${client.totalSpent.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {client.lastLogin ? (
                        <Typography variant="body2">
                          {new Date(client.lastLogin).toLocaleDateString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Never
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, client)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItemComponent onClick={() => selectedClient && handleViewClient(selectedClient)}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItemComponent>
        <MenuItemComponent onClick={() => selectedClient && handleEditClient(selectedClient)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Client
        </MenuItemComponent>
        {selectedClient?.status === 'active' && (
          <MenuItemComponent onClick={() => selectedClient && handleChangeStatus(selectedClient, 'inactive')}>
            <ScheduleIcon sx={{ mr: 1 }} fontSize="small" />
            Deactivate
          </MenuItemComponent>
        )}
        {selectedClient?.status === 'inactive' && (
          <MenuItemComponent onClick={() => selectedClient && handleChangeStatus(selectedClient, 'active')}>
            <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />
            Activate
          </MenuItemComponent>
        )}
        <MenuItemComponent onClick={() => selectedClient && handleChangeStatus(selectedClient, 'suspended')}>
          <BlockIcon sx={{ mr: 1 }} fontSize="small" />
          Suspend
        </MenuItemComponent>
        <Divider />
        <MenuItemComponent onClick={() => selectedClient && handleDeleteClient(selectedClient)}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Client
        </MenuItemComponent>
      </Menu>

      {/* Create/Edit Client Dialog */}
      <ClientDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleSaveClient}
        client={editingClient}
      />

      {/* Client Detail Dialog */}
      <ClientDetailDialog
        open={clientDetailDialog}
        onClose={() => {
          setClientDetailDialog(false);
          setSelectedClientForDetail(null);
        }}
        client={selectedClientForDetail}
      />
    </Box>
  );
};

// Client Detail Dialog Component
const ClientDetailDialog: React.FC<{
  open: boolean;
  client: Client | null;
  onClose: () => void;
}> = ({ open, client, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [clientProjects, setClientProjects] = useState<ClientProject[]>([]);
  const [clientCommunications, setClientCommunications] = useState<CommunicationLog[]>([]);
  const [clientBilling, setClientBilling] = useState<any>(null);
  const [clientContacts, setClientContacts] = useState<ClientContact[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingCommunications, setLoadingCommunications] = useState(false);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Load client data when dialog opens and client changes
  useEffect(() => {
    if (open && client) {
      loadClientProjects();
      loadClientCommunications();
      loadClientBilling();
      loadClientContacts();
    }
  }, [open, client]);

  const loadClientProjects = async () => {
    if (!client) return;
    
    setLoadingProjects(true);
    try {
      const response = await clientService.getClientProjects(client.id);
      setClientProjects(response.projects);
    } catch (error) {
      console.error('Failed to load client projects:', error);
      setClientProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadClientCommunications = async () => {
    if (!client) return;
    
    setLoadingCommunications(true);
    try {
      const response = await clientService.getClientCommunications(client.id);
      setClientCommunications(response.communications);
    } catch (error) {
      console.error('Failed to load client communications:', error);
      setClientCommunications([]);
    } finally {
      setLoadingCommunications(false);
    }
  };

  const loadClientBilling = async () => {
    if (!client) return;
    
    setLoadingBilling(true);
    try {
      const response = await clientService.getClientBilling(client.id);
      setClientBilling(response);
    } catch (error) {
      console.error('Failed to load client billing:', error);
      setClientBilling(null);
    } finally {
      setLoadingBilling(false);
    }
  };

  const loadClientContacts = async () => {
    if (!client) return;
    
    setLoadingContacts(true);
    try {
      const response = await clientService.getClientContacts(client.id);
      setClientContacts(response.contacts);
    } catch (error) {
      console.error('Failed to load client contacts:', error);
      setClientContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleAddCommunication = () => {
    // TODO: Implement add communication dialog
    console.log('Add communication for client:', client?.id);
    alert(`📧 Add Communication Feature\n\nThis feature will allow you to:\n• Send emails to client contacts\n• Log phone calls and meetings\n• Track communication history\n• Set follow-up reminders\n\nComing in the next release!`);
  };

  const handleCreateInvoice = () => {
    // TODO: Implement create invoice dialog
    console.log('Create invoice for client:', client?.id);
    alert(`💰 Create Invoice Feature\n\nThis feature will allow you to:\n• Generate professional invoices\n• Track payment status\n• Send automated reminders\n• Export to PDF\n• Integration with accounting systems\n\nComing in the next release!`);
  };

  const handleAddContact = () => {
    // TODO: Implement add contact dialog
    console.log('Add contact for client:', client?.id);
    alert(`👤 Add Contact Feature\n\nThis feature will allow you to:\n• Add multiple client contacts\n• Set primary/secondary contacts\n• Track contact roles and departments\n• Manage contact preferences\n• Sync with CRM systems\n\nComing in the next release!`);
  };

  if (!client) return null;

  const getProjectStatusColor = (status: ClientProject['status']) => {
    switch (status) {
      case 'planning': return 'info';
      case 'active': return 'primary';
      case 'on_hold': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getBillingStatusColor = (status: BillingRecord['status']) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getCommunicationIcon = (type: CommunicationLog['type']) => {
    switch (type) {
      case 'email': return <EmailIcon />;
      case 'call': return <CallIcon />;
      case 'meeting': return <MeetingIcon />;
      case 'note': return <NoteIcon />;
      default: return <MessageIcon />;
    }
  };

  const calculateClientStats = () => {
    const totalBudget = clientProjects.reduce((sum, project) => sum + project.budget, 0);
    const totalSpent = clientProjects.reduce((sum, project) => sum + project.spent, 0);
    const activeProjects = clientProjects.filter(p => p.status === 'active' || p.status === 'in_progress').length;
    const completedProjects = clientProjects.filter(p => p.status === 'completed').length;
    
    // Task statistics
    const totalTasks = clientProjects.reduce((sum, project) => sum + (project.taskCount || 0), 0);
    const totalCompletedTasks = clientProjects.reduce((sum, project) => sum + (project.completedTasks || 0), 0);
    const totalHours = clientProjects.reduce((sum, project) => sum + (project.totalHours || 0), 0);
    const totalStoryPoints = clientProjects.reduce((sum, project) => sum + (project.totalStoryPoints || 0), 0);
    const totalEstimate = clientProjects.reduce((sum, project) => sum + (project.projectEstimate || 0), 0);
    
    return { 
      totalBudget, 
      totalSpent, 
      activeProjects, 
      completedProjects,
      totalTasks,
      totalCompletedTasks,
      totalHours,
      totalStoryPoints,
      totalEstimate
    };
  };

  const stats = calculateClientStats();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              {client.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h5">{client.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {client.company} • {client.email}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Client Overview Cards */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Client Overview</Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
            gap: 2,
            mb: 2
          }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">{stats.activeProjects}</Typography>
                <Typography variant="body2" color="text.secondary">Active Projects</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">{stats.completedProjects}</Typography>
                <Typography variant="body2" color="text.secondary">Completed</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main">${(stats.totalBudget / 1000).toFixed(0)}K</Typography>
                <Typography variant="body2" color="text.secondary">Total Budget</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">${(stats.totalSpent / 1000).toFixed(0)}K</Typography>
                <Typography variant="body2" color="text.secondary">Total Spent</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Task Statistics Row */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>Task & Estimation Overview</Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
            gap: 2 
          }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="primary">
                  {stats.totalCompletedTasks}/{stats.totalTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">Tasks Completed</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="info.main">
                  {stats.totalHours.toFixed(1)}h
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Hours</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="secondary.main">
                  {stats.totalStoryPoints.toFixed(0)} SP
                </Typography>
                <Typography variant="body2" color="text.secondary">Story Points</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="success.main">
                  ${(stats.totalEstimate / 1000).toFixed(1)}K
                </Typography>
                <Typography variant="body2" color="text.secondary">Project Estimate</Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Tabs for different sections */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Projects" icon={<ProjectIcon />} />
            <Tab label="Communications" icon={<MessageIcon />} />
            <Tab label="Billing" icon={<BillingIcon />} />
            <Tab label="Contacts" icon={<ContactIcon />} />
          </Tabs>
        </Box>

        {/* Projects Tab */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Client Projects</Typography>
              <Button variant="outlined" size="small">
                Create New Project
              </Button>
            </Box>
            
            {loadingProjects ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography>Loading projects...</Typography>
              </Box>
            ) : clientProjects.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No projects found for this client</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Project</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Spent</TableCell>
                      <TableCell>Timeline</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientProjects.map((project) => (
                      <TableRow key={project.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{project.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              PM: {project.projectManager || 'Unassigned'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={project.status.replace('_', ' ')} 
                            color={getProjectStatusColor(project.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={project.progress} 
                              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="body2">{project.progress}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>${project.budget.toLocaleString()}</TableCell>
                        <TableCell>${project.spent.toLocaleString()}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(project.startDate).toLocaleDateString()}
                          </Typography>
                          {project.endDate && (
                            <Typography variant="body2" color="text.secondary">
                              to {new Date(project.endDate).toLocaleDateString()}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Communications Tab */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Communication History</Typography>
              <Button variant="outlined" size="small" onClick={handleAddCommunication}>
                Add Communication
              </Button>
            </Box>
            
            {loadingCommunications ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : clientCommunications.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No communications found for this client.
                </Typography>
              </Paper>
            ) : (
              <List>
                {clientCommunications.map((comm) => (
                  <ListItem key={comm.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                    <ListItemIcon>
                      {getCommunicationIcon(comm.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2">{comm.subject}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(comm.date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {comm.content}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Type: {comm.type} • Participants: {comm.participants.join(', ')} • Created by: {comm.createdBy}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Billing Tab */}
        {activeTab === 2 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Billing Overview</Typography>
              <Button variant="outlined" size="small" onClick={handleCreateInvoice}>
                Create Invoice
              </Button>
            </Box>
            
            {loadingBilling ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : !clientBilling ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No billing information found for this client.
                </Typography>
              </Paper>
            ) : (
              <Box>
                {/* Billing Summary */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mb: 3 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      ${clientBilling.summary?.totalBudget?.toLocaleString() || '0'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Budget
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      ${clientBilling.summary?.totalSpent?.toLocaleString() || '0'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Spent
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="warning.main">
                      ${clientBilling.summary?.remainingBudget?.toLocaleString() || '0'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Remaining
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="info.main">
                      {clientBilling.summary?.activeProjects || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Projects
                    </Typography>
                  </Paper>
                </Box>

                {/* Invoices */}
                <Typography variant="h6" sx={{ mb: 2 }}>Invoices</Typography>
                {!clientBilling.invoices || clientBilling.invoices.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      No invoices found for this client.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Invoices will be available once the invoicing system is implemented.
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={handleCreateInvoice}
                      sx={{ mt: 2 }}
                    >
                      Create Invoice (Coming Soon)
                    </Button>
                  </Paper>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Invoice</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Issue Date</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clientBilling.invoices.map((invoice: any) => (
                          <TableRow key={invoice.id}>
                            <TableCell>
                              <Typography variant="subtitle2">{invoice.id}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                ${invoice.amount.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={invoice.status} 
                                color={getBillingStatusColor(invoice.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                            <TableCell>{new Date(invoice.dueDate).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {invoice.description}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Contacts Tab */}
        {activeTab === 3 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Client Contacts</Typography>
              <Button variant="outlined" size="small" onClick={handleAddContact}>
                Add Contact
              </Button>
            </Box>
            
            {loadingContacts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : clientContacts.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No contacts found for this client.
                </Typography>
              </Paper>
            ) : (
              <List>
                {clientContacts.map((contact) => (
                  <ListItem key={contact.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: contact.isPrimary ? 'primary.main' : 'grey.500' }}>
                        {contact.name.split(' ').map((n: string) => n[0]).join('')}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">{contact.name}</Typography>
                          {contact.isPrimary && (
                            <Chip label="Primary" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2">{contact.role}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {contact.email} • {contact.phone}
                          </Typography>
                          {contact.department && (
                            <Typography variant="caption" color="text.secondary">
                              {contact.department}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Client Create/Edit Dialog Component
interface ClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (client: Partial<Client>) => void;
  client?: Client | null;
}

const ClientDialog: React.FC<ClientDialogProps> = ({ open, onClose, onSave, client }) => {
  const [formData, setFormData] = useState<Partial<Client>>({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'active'
  });

  useEffect(() => {
    if (client) {
      setFormData(client);
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        status: 'active'
      });
    }
  }, [client, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field: keyof Client) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {client ? 'Edit Client' : 'Create New Client'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={handleChange('name')}
              required
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              required
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={handleChange('phone')}
            />
            <TextField
              fullWidth
              label="Company"
              value={formData.company}
              onChange={handleChange('company')}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {client ? 'Update Client' : 'Create Client'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClientManagement;